/*
 * Ring size finder ("Ringgröße ermitteln") on ring product pages.
 *
 * The German ring size is the inner circumference in mm. A finger circumference is the size
 * itself; an inner diameter d gives the circumference pi * d. The pure part below is exposed as
 * window.RingSizeFinder and tested in cli/test/pdp-ring-size-finder.test.ts; the DOM part wires
 * the dialog of snippets/ring-size-finder.liquid. No storage, no tracking, no network.
 */
(function (root) {
  'use strict';

  var LIMITS = {
    circumference: { min: 38, max: 76 },
    diameter: { min: 12, max: 24.2 }
  };

  /** "17,2", " 54 ", "17,2 mm" -> number; anything else -> NaN. */
  function parseMm(raw) {
    if (typeof raw !== 'string') return NaN;
    var text = raw.trim().replace(/\s*mm$/i, '').replace(',', '.');
    if (!/^\d+(?:\.\d+)?$/.test(text)) return NaN;
    return Number(text);
  }

  function convert(mode, raw) {
    var limits = LIMITS[mode];
    if (!limits) throw new Error('unknown mode ' + mode);
    var value = parseMm(raw);
    if (!isFinite(value) || value < limits.min || value > limits.max) {
      return { ok: false, min: limits.min, max: limits.max };
    }
    var circumference = mode === 'diameter' ? Math.PI * value : value;
    var diameter = mode === 'diameter' ? value : value / Math.PI;
    return { ok: true, size: Math.round(circumference), circumference: circumference, diameter: diameter };
  }

  function formatNumber(value, locale, minDigits, maxDigits) {
    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: minDigits,
        maximumFractionDigits: maxDigits
      }).format(value);
    } catch (e) {
      var fixed = value.toFixed(maxDigits);
      if (minDigits < maxDigits) fixed = String(Number(fixed));
      return /^de/i.test(locale || '') ? fixed.replace('.', ',') : fixed;
    }
  }

  // The size half of an option value: "Weite 54", "54", "Ringgröße 56", "Größe 50", "54,5".
  // A metal half ("18 kt Weißgold") or a stone weight ("0,25 ct") is no size.
  var SIZE_PART = /^(?:(?:ring)?(?:weite|gr(?:ö|oe)(?:ß|ss)e|size)|ring\s+size|gr\.)?\s*(\d{2}(?:[.,]\d+)?)(?:\s*mm)?$/i;

  function sizeFromPart(part) {
    var match = SIZE_PART.exec(String(part || '').trim());
    if (!match) return null;
    var size = Number(match[1].replace(',', '.'));
    return size >= 30 && size <= 80 ? size : null;
  }

  // A combined option value reads "18 kt Weißgold · Weite 54".
  var COMBINED_SEPARATOR = ' · ';

  /** The size of a variant, the option text that carries it, and every other option text. */
  function splitVariant(variant, sizeOptions) {
    var options = [variant.option1, variant.option2, variant.option3];
    var size = null;
    var sizePart = null;
    var rest = [];
    for (var i = 0; i < options.length; i++) {
      var value = options[i];
      if (value == null) continue;
      if (sizeOptions.indexOf(i) === -1) {
        rest.push(String(value));
        continue;
      }
      var parts = String(value).split(COMBINED_SEPARATOR);
      var found = false;
      for (var p = 0; p < parts.length; p++) {
        var s = sizeFromPart(parts[p]);
        if (s !== null && !found) {
          size = s;
          sizePart = parts[p];
          found = true;
        } else {
          rest.push(parts[p]);
        }
      }
    }
    return { size: size, sizePart: sizePart, rest: rest.join('|') };
  }

  // Float slack for a circumference computed as pi * d.
  var EPSILON = 1e-9;

  /**
   * The listed size that fits a measured circumference: the nearest one in the window
   * (circumference - 0.5, circumference + 0.5], the larger one on a tie. For whole sizes this is
   * exactly Math.round; a listed half size ("Weite 52,5") is a size of its own. null: none fits.
   */
  function nearestSize(sizes, circumference) {
    var best = null;
    for (var i = 0; i < sizes.length; i++) {
      var s = sizes[i];
      if (typeof s !== 'number' || !isFinite(s)) continue;
      var delta = s - circumference;
      if (delta <= -0.5 + EPSILON || delta > 0.5 + EPSILON) continue;
      if (best === null) {
        best = s;
        continue;
      }
      var gap = Math.abs(delta);
      var bestGap = Math.abs(best - circumference);
      if (gap < bestGap - EPSILON || (Math.abs(gap - bestGap) <= EPSILON && s > best)) best = s;
    }
    return best;
  }

  /**
   * The variant whose listed size fits the measured circumference (see nearestSize), in
   * everything else the current variant has.
   */
  function findVariant(variants, sizeOptions, currentId, circumference) {
    if (!sizeOptions || !sizeOptions.length) return null;
    var current = null;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(currentId)) current = variants[i];
    }
    if (!current) return null;
    var want = splitVariant(current, sizeOptions).rest;
    var candidates = [];
    for (var j = 0; j < variants.length; j++) {
      var parts = splitVariant(variants[j], sizeOptions);
      if (parts.size !== null && parts.rest === want) candidates.push({ variant: variants[j], parts: parts });
    }
    var size = nearestSize(candidates.map(function (c) { return c.parts.size; }), circumference);
    if (size === null) return null;
    for (var k = 0; k < candidates.length; k++) {
      if (candidates[k].parts.size === size) {
        return {
          variant: candidates[k].variant,
          sizePart: candidates[k].parts.sizePart,
          current: String(candidates[k].variant.id) === String(currentId),
          size: size
        };
      }
    }
    return null;
  }

  /** Selects a variant through the picker's own radios and their change event. */
  function selectVariant(scope, variant, sizePart) {
    var EventCtor = root.Event || (typeof Event !== 'undefined' ? Event : null);
    var fire = function (el) {
      el.dispatchEvent(new EventCtor('change', { bubbles: true }));
    };
    var combined = scope.querySelector('[data-combined]');
    if (combined && !combined.hidden && sizePart) {
      var chips = combined.querySelectorAll('[data-combined-size]');
      for (var c = 0; c < chips.length; c++) {
        if (chips[c].value === sizePart && !chips[c].disabled) {
          /* A chip is a radio, or an <option> of the size select (more than 8 sizes). */
          if (chips[c].tagName === 'OPTION') chips[c].selected = true;
          else chips[c].checked = true;
          fire(chips[c]);
          return;
        }
      }
    }
    var values = [variant.option1, variant.option2, variant.option3];
    var changed = null;
    for (var i = 0; i < values.length; i++) {
      if (values[i] == null) continue;
      var radios = scope.querySelectorAll('[data-option-index="' + i + '"]');
      for (var r = 0; r < radios.length; r++) {
        var should = radios[r].value === values[i];
        if (should && !radios[r].checked) changed = radios[r];
        radios[r].checked = should;
      }
    }
    if (changed) fire(changed);
  }

  /**
   * Puts the size into the prepared inquiry (contact form link); its body ends on
   * "Gewünschte Ringweite: ". The size goes before the #kontaktformular anchor, not after it.
   */
  function inquiryHref(href, size) {
    if (!/[?&]body=/.test(href)) return href;
    var hash = href.indexOf('#');
    var base = hash < 0 ? href : href.slice(0, hash);
    return base + encodeURIComponent(String(size)) + (hash < 0 ? '' : href.slice(hash));
  }

  /**
   * The fixed size of a ring without a size picker: the current variant's own ring size
   * (custom.ring_size in the variant data, "52" or "52,5"), else the size read from the text
   * (staticSize). A value that is no ring size counts as none.
   */
  function fixedSize(variants, currentId, staticSize) {
    for (var i = 0; i < (variants || []).length; i++) {
      if (String(variants[i].id) !== String(currentId)) continue;
      var own = sizeFromPart(variants[i].ring_size);
      if (own !== null) return own;
    }
    return staticSize;
  }

  /** The variants of the product section: `.product` carries data-variant-json itself. */
  function readVariants(productEl) {
    if (!productEl) return [];
    var holder = productEl.hasAttribute && productEl.hasAttribute('data-variant-json')
      ? productEl
      : (productEl.querySelector ? productEl.querySelector('[data-variant-json]') : null);
    if (!holder) return [];
    try {
      var list = JSON.parse(holder.getAttribute('data-variant-json'));
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  var api = {
    LIMITS: LIMITS,
    parseMm: parseMm,
    convert: convert,
    formatNumber: formatNumber,
    sizeFromPart: sizeFromPart,
    nearestSize: nearestSize,
    findVariant: findVariant,
    fixedSize: fixedSize,
    selectVariant: selectVariant,
    inquiryHref: inquiryHref,
    readVariants: readVariants
  };
  root.RingSizeFinder = api;

  if (!root.document) return;

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, function (all, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : all;
    });
  }

  function init(dialog) {
    if (dialog.__ringSizeFinder) return;
    dialog.__ringSizeFinder = true;
    var data = dialog.dataset;
    var locale = data.locale || document.documentElement.lang || 'de';
    var productEl = dialog.closest('.product[data-variant-json]') || document;
    var sizeOptions = String(data.sizeOptions || '')
      .split(',')
      .filter(function (s) { return s !== ''; })
      .map(Number);
    var staticSize = data.staticSize ? sizeFromPart(data.staticSize) : null;

    var form = dialog.querySelector('[data-ring-size-finder-form]');
    var input = dialog.querySelector('[data-ring-size-finder-input]');
    var label = dialog.querySelector('[data-ring-size-finder-label]');
    var hint = dialog.querySelector('[data-ring-size-finder-hint]');
    var error = dialog.querySelector('[data-ring-size-finder-error]');
    var result = dialog.querySelector('[data-ring-size-finder-result]');
    var sizeOut = dialog.querySelector('[data-ring-size-finder-size]');
    var equivalent = dialog.querySelector('[data-ring-size-finder-equivalent]');
    var status = dialog.querySelector('[data-ring-size-finder-status]');
    var selectBtn = dialog.querySelector('[data-ring-size-finder-select]');
    var inquiry = dialog.querySelector('[data-ring-size-finder-inquiry]');
    var inquiryLink = inquiry ? inquiry.querySelector('[data-ring-size-finder-inquiry-link]') : null;
    var resetBtn = dialog.querySelector('[data-ring-size-finder-reset]');
    var live = dialog.querySelector('[data-ring-size-finder-live]');
    var opener = null;
    var match = null;

    function mode() {
      var checked = dialog.querySelector('[data-ring-size-finder-mode]:checked');
      return checked && checked.value === 'diameter' ? 'diameter' : 'circumference';
    }

    function range(m) {
      var limits = LIMITS[m];
      return { min: formatNumber(limits.min, locale, 0, 1), max: formatNumber(limits.max, locale, 0, 1) };
    }

    function syncMode() {
      var m = mode();
      label.textContent = m === 'diameter' ? data.textLabelDiameter : data.textLabelCircumference;
      hint.textContent = fill(data.textHint, range(m));
      clearError();
    }

    function clearError() {
      error.hidden = true;
      error.textContent = '';
      input.removeAttribute('aria-invalid');
    }

    function announce(text) {
      live.textContent = '';
      // A new text in the next frame is read even when it equals the last one.
      root.setTimeout(function () { live.textContent = text; }, 50);
    }

    function currentVariantId() {
      var idInput = productEl.querySelector('[data-variant-id]');
      return idInput ? idInput.value : null;
    }

    function variants() {
      return readVariants(productEl);
    }

    function showForm() {
      result.hidden = true;
      form.hidden = false;
      match = null;
      input.focus();
      input.select();
    }

    function showResult(converted) {
      var m = mode();
      selectBtn.hidden = true;
      if (inquiry) inquiry.hidden = true;
      // A listed size within half a millimetre wins over the rounded one, so a ring listed
      // as "Weite 52,5" is selectable and the result shows the size the button selects.
      match = sizeOptions.length ? findVariant(variants(), sizeOptions, currentVariantId(), converted.circumference) : null;
      // A ring without a size picker has the size of the variant selected NOW (its metals may
      // differ: "52" / "52,5"), else the size read from its text.
      var fixed = !sizeOptions.length ? fixedSize(variants(), currentVariantId(), staticSize) : null;
      var staticMatch = fixed !== null
        && nearestSize([fixed], converted.circumference) !== null;
      var shown = match ? match.size : (staticMatch ? fixed : converted.size);
      var size = formatNumber(shown, locale, 0, 1);
      sizeOut.textContent = size;
      equivalent.textContent = m === 'diameter'
        ? fill(data.textEquivalentCircumference, { value: formatNumber(converted.circumference, locale, 1, 1) })
        : fill(data.textEquivalentDiameter, { value: formatNumber(converted.diameter, locale, 1, 1) });


      var message;
      if (match && match.current) {
        message = fill(data.textSelected, { size: size });
      } else if (match) {
        message = '';
        selectBtn.textContent = fill(data.textSelect, { size: size });
        selectBtn.hidden = false;
      } else if (staticMatch) {
        message = fill(data.textStaticMatch, { size: size });
      } else {
        message = fill(data.textNotListed, { size: size });
        if (inquiry && inquiryLink) {
          var source = productEl.querySelector('[data-size-inquiry]');
          if (source) {
            inquiryLink.setAttribute('href', inquiryHref(source.getAttribute('href'), size));
            inquiry.hidden = false;
          }
        }
      }
      status.textContent = message;
      status.hidden = !message;

      form.hidden = true;
      result.hidden = false;
      announce([fill(data.textAnnouncement, { size: size }), equivalent.textContent, message].filter(Boolean).join(' '));
      (selectBtn.hidden ? resetBtn : selectBtn).focus();
    }

    var modeInputs = dialog.querySelectorAll('[data-ring-size-finder-mode]');
    for (var i = 0; i < modeInputs.length; i++) modeInputs[i].addEventListener('change', syncMode);
    input.addEventListener('input', clearError);

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var converted = convert(mode(), input.value);
      if (!converted.ok) {
        var text = fill(data.textError, range(mode()));
        error.textContent = text;
        error.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        announce(text);
        input.focus();
        return;
      }
      clearError();
      showResult(converted);
    });

    resetBtn.addEventListener('click', showForm);

    selectBtn.addEventListener('click', function () {
      if (!match) return;
      selectVariant(productEl, match.variant, match.sizePart);
      dialog.close();
    });

    dialog.querySelector('[data-ring-size-finder-close]').addEventListener('click', function () {
      dialog.close();
    });
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', function () {
      if (opener && typeof opener.focus === 'function') opener.focus();
      opener = null;
    });

    dialog.__open = function (trigger) {
      opener = trigger;
      if (typeof dialog.showModal !== 'function') {
        root.location.href = data.guideUrl || '/pages/ringgroesse-finden';
        return;
      }
      result.hidden = true;
      form.hidden = false;
      syncMode();
      dialog.showModal();
      input.focus();
    };
  }

  function onOpen(event) {
    // The trigger is a link to the guide page: a click with a modifier or another button
    // opens that page as the browser would.
    if (event.defaultPrevented || (event.button != null && event.button !== 0)) return;
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    var trigger = event.target.closest ? event.target.closest('[data-ring-size-finder-open]') : null;
    if (!trigger) return;
    var dialog = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!dialog) return;
    event.preventDefault();
    init(dialog);
    dialog.__open(trigger);
  }

  document.addEventListener('click', onOpen);
})(typeof window !== 'undefined' ? window : this);
